import { PROTOCOL_VERSION, type JsonMessage } from '../../../shared/protocol';

export type SocketStatus = 'connecting' | 'online' | 'reconnecting' | 'offline';

export class ReliableSocket {
  private socket?: WebSocket;
  private closed = false;
  private reconnectTimer?: number;
  private backoff = 500;
  private pingTimer?: number;
  private readonly queue: JsonMessage[] = [];
  private readonly listeners = new Set<(value: JsonMessage) => void>();

  constructor(
    private readonly hello: () => JsonMessage,
    private readonly onStatus: (status: SocketStatus) => void,
    private readonly onBinary: (value: ArrayBuffer) => void,
  ) {
    this.connect();
    const wake = (): void => {
      if (document.visibilityState === 'visible') {
        this.connect();
      }
    };
    window.addEventListener('online', wake);
    window.addEventListener('pageshow', wake);
    document.addEventListener('visibilitychange', wake);
  }

  subscribe(listener: (value: JsonMessage) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private connect(): void {
    if (
      this.closed ||
      (this.socket &&
        (this.socket.readyState === WebSocket.OPEN ||
          this.socket.readyState === WebSocket.CONNECTING))
    ) {
      return;
    }

    this.onStatus(this.backoff > 500 ? 'reconnecting' : 'connecting');
    const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const socket = new WebSocket(`${protocol}//${location.host}/ws`);
    socket.binaryType = 'arraybuffer';
    this.socket = socket;

    socket.onopen = (): void => {
      this.backoff = 500;
      this.onStatus('online');
      this.send(this.hello());
      for (const value of this.queue.splice(0)) {
        this.send(value);
      }
      this.pingTimer = window.setInterval(() => {
        this.send({ v: PROTOCOL_VERSION, type: 'ping', at: Date.now() } as JsonMessage);
      }, 20_000);
    };

    socket.onmessage = (event): void => {
      if (typeof event.data === 'string') {
        try {
          const value = JSON.parse(event.data) as JsonMessage;
          for (const listener of this.listeners) {
            listener(value);
          }
        } catch {
          /* ignore malformed server data */
        }
      } else if (event.data instanceof ArrayBuffer) {
        this.onBinary(event.data);
      } else if (event.data instanceof Blob) {
        void event.data.arrayBuffer().then((value) => this.onBinary(value));
      }
    };

    socket.onerror = (): void => {
      /* close schedules the reconnect */
    };

    socket.onclose = (): void => {
      if (this.pingTimer) {
        window.clearInterval(this.pingTimer);
        this.pingTimer = undefined;
      }
      this.socket = undefined;
      if (this.closed) {
        this.onStatus('offline');
        return;
      }
      this.onStatus('reconnecting');
      this.scheduleReconnect();
    };
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer || this.closed) return;
    const delay = this.backoff;
    this.backoff = Math.min(8_000, this.backoff * 2);
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = undefined;
      this.connect();
    }, delay);
  }

  send(value: JsonMessage): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(value));
    } else if (this.queue.length < 100) {
      this.queue.push(value);
    }
  }

  sendBinary(value: ArrayBuffer): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(value);
    }
  }

  isOpen(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  close(): void {
    this.closed = true;
    if (this.reconnectTimer) {
      window.clearTimeout(this.reconnectTimer);
    }
    if (this.pingTimer) {
      window.clearInterval(this.pingTimer);
    }
    this.socket?.close();
  }
}
